import React, { createContext, useContext, useEffect, useState, ReactNode, use } from 'react';
import { useLocation } from 'react-router-dom';
import { ClientLogger } from '../lib/client-logger';
import { useStateWithRef } from '../lib/hooks';

// Create the context for storing the last visited path+query
const QueryPersistenceContext = createContext({
  params: {} as ParamsFormat, // the query parameters from the last path
  paramsForCurrentUser: (): ParamsForUser | undefined => undefined, // function to get params for the current user
});

// Custom hook for easy context consumption
export const useQueryPersistence = () => useContext(QueryPersistenceContext);

// Define the type for the provider's props
interface QueryPersistenceProviderProps {
  children: ReactNode; // ReactNode allows any valid React child (string, JSX, etc.)
}

const DEBUG = true;

const STORAGE_KEY = 'queryParams';

function storageKey(asUserId: string): string {
  return `${STORAGE_KEY}-${asUserId}`;
}

interface ParamsForUser {
  params: Record<string, string>;
  lastPath: string | undefined; // last visited path with query params
}

interface ParamsFormat {
  [asUserId: string]: ParamsForUser;
}

function readStorage(asUserId: string): ParamsFormat {
  const storedData = sessionStorage.getItem(storageKey(asUserId)); // sessionStorage is used for temporary storage
  if (storedData) {
    try {
      return JSON.parse(storedData);
    } catch (error) {
      ClientLogger.error('QueryPersistence.readStorage', 'Failed to parse stored query params:', error);
    }
  }
  return {};
}
function writeStorage(asUserId: string, data: ParamsFormat): void {
  try {
    sessionStorage.setItem(storageKey(asUserId), JSON.stringify(data)); // sessionStorage is used for temporary storage
  } catch (error) {
    ClientLogger.error('QueryPersistence.writeStorage', 'Failed to write query params to sessionStorage:', error);
  }
}

export const QueryPersistenceProvider: React.FC<QueryPersistenceProviderProps> = ({ children }) => {
  const location = useLocation();
  const [asUserId, setAsUserId] = useState<string | null | 'asuser-not-set'>(null);
  const [params, setParams, paramsRef] = useStateWithRef<ParamsFormat>({});

  useEffect(() => {
    // Initialize from localStorage (if available) on first load
    const searchParams = new URLSearchParams(location.search);
    const asUserIdParam = searchParams.get('asUserId') || 'asuser-not-set';

    const savedParams = readStorage(asUserIdParam);
    DEBUG &&
      ClientLogger.debug('QueryPersistenceProvider', 'useState - queryParams from localStorage', {
        savedParams,
      });
    // Add params from the URL
    const newParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      newParams[key] = value;
    });

    setAsUserId(asUserIdParam);
    savedParams[asUserIdParam] = savedParams[asUserIdParam] || {
      params: newParams,
      lastPath: window.location.pathname + window.location.search,
    };
    writeStorage(asUserIdParam, savedParams);
    setParams(savedParams);
    DEBUG &&
      ClientLogger.debug('QueryPersistenceProvider', 'useEffect - initial params', {
        asUserId: asUserIdParam,
        params: savedParams[asUserIdParam].params,
        lastPath: savedParams[asUserIdParam].lastPath,
      });
  }, []);

  useEffect(() => {
    if (asUserId) {
      const fullPath = location.pathname + location.search;
      // Optionally skip certain routes (like auth callback or login) to avoid overwriting the original saved route:
      // if (location.pathname === '/login' || location.pathname === '/oauth/callback') {
      //   return; // don't update storage for login or OAuth callback routes
      // }
      const newParams: ParamsFormat = { ...paramsRef.current };
      newParams[asUserId] = { params: newParams[asUserId].params, lastPath: fullPath };
      writeStorage(asUserId, newParams);
      DEBUG &&
        ClientLogger.debug('QueryPersistenceProvider', 'useEffect - location setter', {
          lastPath: fullPath,
          location: { pathname: location.pathname, search: location.search },
        });
      setParams(newParams);
    }
  }, [location, asUserId]);

  DEBUG &&
    ClientLogger.debug('QueryPersistenceProvider', 'useEffect', {
      asUserId,
      paramsCurrent: paramsRef.current,
      params,
      location: { pathname: location.pathname, search: location.search },
    });

  const paramsForCurrentUser = (): ParamsForUser | undefined => {
    if (!asUserId) {
      return undefined; // No user ID set, cannot retrieve params
    }
    const currentParams = paramsRef.current ? paramsRef.current[asUserId] : undefined;
    if (!currentParams) {
      return undefined; // No params found for the current user
    }
    return {
      params: currentParams.params,
      lastPath: currentParams.lastPath,
    };
  };
  return <QueryPersistenceContext.Provider value={{ params, paramsForCurrentUser }}>{children}</QueryPersistenceContext.Provider>;
};
